import React, { useEffect, useRef, useState } from 'react';
import { listValuations, deleteValuation, getValuation } from '../api/valuations';
import { getValuationPdf } from '../api/pdf';
import { getProfile } from '../api/profile';
import SaveDialog from '../components/SaveDialog';
import ShareDialog from '../components/ShareDialog';
import { describeError } from '../lib/describeError';
import { showToast } from '../lib/toast';

const TIER_TONES = {
  Entry: { bg: 'var(--line-dim)', fg: 'var(--muted)' },
  Competitive: { bg: '#F0EAD8', fg: '#5E513C' },
  Standard: { bg: 'var(--accent-bg)', fg: 'var(--accent)' },
  'High-Value': { bg: '#E3E5EE', fg: '#3D4A77' },
  Premium: { bg: '#ECE1ED', fg: '#6B3F75' },
  'Ultra-Premium': { bg: 'linear-gradient(135deg, #D6E0D2 0%, #E5EBE3 100%)', fg: 'var(--ink)' },
};

export default function Dashboard({ user, onOpen, onNew, onPickPreset, onOpenAccountPreferences }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [phiAttestationRequired, setPhiAttestationRequired] = useState(false);
  const [renameTarget, setRenameTarget] = useState(null);
  const [shareTarget, setShareTarget] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => { reload(); }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const profile = await getProfile();
        if (!cancelled) setPhiAttestationRequired(Boolean(profile.phi_attestation_required));
      } catch {
        /* ignore — list error or profile gate handles UX */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function reload() {
    setError(null);
    try {
      const resp = await listValuations();
      setRows(resp.valuations || []);
    } catch (err) {
      setRows([]);
      setError(err?.code || 'list_failed');
    }
  }

  async function handleOpen(row) {
    try {
      const full = await getValuation(row.public_id);
      onOpen({
        inputs: full.inputs,
        name: full.name,
        publicId: full.public_id,
        updatedAt: full.updated_at,
      });
    } catch (err) {
      setError(err?.code || 'open_failed');
    }
  }

  async function handleDelete(row) {
    if (!window.confirm(`Delete "${row.name}"? You can recreate it from a preset later, but the saved row will be gone.`)) {
      return;
    }
    try {
      await deleteValuation(row.public_id);
      setRows((cur) => (cur || []).filter((r) => r.public_id !== row.public_id));
      showToast(`Deleted "${row.name}".`, { tone: 'warn' });
    } catch (err) {
      setError(err?.code || 'delete_failed');
    }
  }

  async function handleDownload(row) {
    if (downloadingId) return;
    setDownloadingId(row.public_id);
    setError(null);
    try {
      const blob = await getValuationPdf(row.public_id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(row.name || 'valuation').replace(/[^a-z0-9]+/gi, '-')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err?.code || 'pdf_failed');
    } finally {
      setDownloadingId(null);
    }
  }

  const stats = computeStats(rows);
  const firstName = (user?.name || user?.email || 'there').split(/\s+|@/)[0];
  // Hide the row-count, the top New-valuation CTA, and the all-zero stats
  // strip when the dashboard is empty — the two-path empty-state card
  // below carries the same CTAs and "0 saved / 0 total / $0" was just
  // visual noise on a first-time landing.
  const hasRows = rows && rows.length > 0;
  const stillLoading = rows === null;

  return (
    <div style={pageStyle} data-testid="dashboard">
      <header style={heroStyle}>
        <div>
          <div style={eyebrowStyle}>Welcome back, {firstName}.</div>
          <h1 style={titleStyle}>My valuations.</h1>
          {(hasRows || stillLoading) && (
            <div style={metaStyle}>{stillLoading ? 'Loading…' : `${rows.length} saved`}</div>
          )}
        </div>
        {hasRows && (
          <NewValuationMenu onNew={onNew} onPickPreset={onPickPreset || onNew} />
        )}
      </header>

      {hasRows && (
        <div style={statsRowStyle}>
          <Stat label="Total valuations" value={stats.total} />
          <Stat label="Most common tier" value={stats.medianTier} display />
          <Stat label="Total est. pharma annual" value={stats.totalPharma} />
        </div>
      )}

      {phiAttestationRequired && (
        <div style={bannerStyle} role="status" data-testid="phi-attestation-banner">
          Before you use the calculator, confirm the data-entry attestation in{' '}
          <button
            type="button"
            style={inlineLinkStyle}
            onClick={() => typeof onOpenAccountPreferences === 'function' && onOpenAccountPreferences()}
          >
            Account preferences
          </button>
          {' '}(profile menu → Account preferences → Data-entry attestation).
        </div>
      )}

      {error && (
        <div style={errorStyle} role="status">
          {describeError('list', error)}
          {(error === 'phi_attestation_required' && typeof onOpenAccountPreferences === 'function') && (
            <>
              {' '}
              <button type="button" style={inlineLinkStyle} onClick={onOpenAccountPreferences}>
                Open Account preferences
              </button>
            </>
          )}
          {' '}
          <button type="button" onClick={reload} style={inlineLinkStyle}>Retry</button>
        </div>
      )}

      {rows !== null && rows.length === 0 && !error && (
        <div style={emptyStyle} data-testid="dashboard-empty">
          <div style={emptyEyebrowStyle}>No saved valuations yet</div>
          <h2 style={emptyHeroStyle}>Two ways to begin.</h2>
          <p style={emptyBodyStyle}>
            Start from scratch and enter every input yourself, or seed the wizard from a sample
            dataset profile and edit what doesn't match. Either path lands you in the same wizard;
            saved valuations show up here once you save your first.
          </p>
          <div style={emptyPathsStyle}>
            <button type="button" onClick={onNew} style={emptyPathPrimaryStyle} data-testid="empty-new">
              <div style={emptyPathTitleStyle}>Start from scratch</div>
              <div style={emptyPathBodyStyle}>Empty wizard. Rate each factor, enter your cohort, pick consent and partnership terms.</div>
              <div style={emptyPathArrowStyle}>Open empty wizard →</div>
            </button>
            <button type="button" onClick={onPickPreset || onNew} style={emptyPathSecondaryStyle} data-testid="empty-preset">
              <div style={emptyPathTitleStyle}>Use a preset</div>
              <div style={emptyPathBodyStyle}>Seven sample profiles, from Academic Biobank to Drug Target Validation. Edit any input afterward.</div>
              <div style={emptyPathArrowStyle}>Pick a starting point →</div>
            </button>
          </div>
        </div>
      )}

      {rows && rows.length > 0 && (
        <div style={tableStyle}>
          <div style={{ ...rowStyle, ...headRowStyle }}>
            <span>Valuation</span>
            <span>Tier</span>
            <span style={{ textAlign: 'right' }}>Pharma annual</span>
            <span>Updated</span>
            <span>Actions</span>
          </div>
          {rows.map((row) => (
            <DashboardRow
              key={row.public_id}
              row={row}
              onOpen={() => handleOpen(row)}
              onShare={() => setShareTarget(row)}
              onRename={() => setRenameTarget(row)}
              onDelete={() => handleDelete(row)}
              onDownload={() => handleDownload(row)}
              downloading={downloadingId === row.public_id}
            />
          ))}
        </div>
      )}

      {renameTarget && (
        <SaveDialog
          mode="rename"
          publicId={renameTarget.public_id}
          initialName={renameTarget.name}
          onSaved={(updated) => {
            setRows((cur) => (cur || []).map((r) => (r.public_id === updated.public_id ? { ...r, name: updated.name } : r)));
            setRenameTarget(null);
            showToast(`Renamed to "${updated.name}".`, { tone: 'success' });
          }}
          onClose={() => setRenameTarget(null)}
        />
      )}

      {shareTarget && (
        <ShareDialog
          publicId={shareTarget.public_id}
          valuationName={shareTarget.name}
          onClose={() => setShareTarget(null)}
        />
      )}
    </div>
  );
}

function DashboardRow({ row, onOpen, onShare, onRename, onDelete, onDownload, downloading }) {
  const tone = TIER_TONES[row.tier] || { bg: 'var(--line-dim)', fg: 'var(--muted)' };
  return (
    <div style={rowStyle} data-row={row.public_id}>
      <button type="button" onClick={onOpen} style={nameBtnStyle} data-action="open">
        <div style={nameStyle}>{row.name}</div>
        <div style={subStyle}>
          {row.status === 'draft' ? 'Draft · ' : 'Final · '}
          {formatTimestamp(row.updated_at)}
        </div>
      </button>
      <span style={{ ...tierChipStyle, background: tone.bg, color: tone.fg }}>{row.tier || '—'}</span>
      <span style={amountStyle}>{formatCompact(row.pharma_annual_usd)}</span>
      <span style={timeStyle}>{formatTimestamp(row.updated_at)}</span>
      <div style={actionsCellStyle}>
        <button type="button" onClick={onOpen} style={primaryActionStyle}>Open</button>
        <button type="button" onClick={onShare} style={secondaryActionStyle}>Share</button>
        <button type="button" onClick={onRename} style={secondaryActionStyle}>Rename</button>
        <button type="button" onClick={onDownload} disabled={downloading} style={downloading ? actionDisabledStyle : secondaryActionStyle}>
          {downloading ? 'Generating…' : 'PDF'}
        </button>
        <button type="button" onClick={onDelete} style={dangerActionStyle}>Delete</button>
      </div>
    </div>
  );
}

function NewValuationMenu({ onNew, onPickPreset }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    function onPointerDown(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    function onKey(e) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  function pickScratch() {
    setOpen(false);
    onNew();
  }

  function pickPreset() {
    setOpen(false);
    onPickPreset();
  }

  return (
    <div ref={wrapRef} style={menuWrapStyle}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={ctaStyle}
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid="new-valuation-menu"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
        New valuation
        <svg width="12" height="12" viewBox="0 0 24 24" aria-hidden="true" style={{ marginLeft: '2px', opacity: 0.85 }}>
          <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
        </svg>
      </button>
      {open && (
        <div role="menu" style={menuPanelStyle} data-testid="new-valuation-dropdown">
          <button type="button" role="menuitem" onClick={pickScratch} style={menuItemStyle} data-testid="new-valuation-scratch">
            <span style={menuItemTitleStyle}>Start from scratch</span>
            <span style={menuItemBodyStyle}>Empty wizard — enter every input yourself.</span>
          </button>
          <button type="button" role="menuitem" onClick={pickPreset} style={menuItemStyle} data-testid="new-valuation-preset">
            <span style={menuItemTitleStyle}>Use a preset</span>
            <span style={menuItemBodyStyle}>Seed from a sample dataset profile, then edit what doesn&apos;t match.</span>
          </button>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, display }) {
  return (
    <div style={statStyle}>
      <div style={statLabelStyle}>{label}</div>
      <div style={display ? statValueDisplayStyle : statValueStyle}>{value}</div>
    </div>
  );
}

function computeStats(rows) {
  if (!rows || rows.length === 0) return { total: '0', medianTier: '—', totalPharma: '$0' };
  const tierCounts = {};
  let totalPharma = 0;
  rows.forEach((r) => {
    if (r.tier) tierCounts[r.tier] = (tierCounts[r.tier] || 0) + 1;
    totalPharma += Number(r.pharma_annual_usd) || 0;
  });
  const ordered = Object.entries(tierCounts).sort((a, b) => b[1] - a[1]);
  const medianTier = ordered.length ? ordered[0][0] : '—';
  return {
    total: String(rows.length),
    medianTier,
    totalPharma: formatCompact(totalPharma),
  };
}

function formatCompact(n) {
  const num = Number(n);
  if (!Number.isFinite(num) || num === 0) return '—';
  const abs = Math.abs(num);
  if (abs >= 1_000_000) {
    const m = Math.round(num / 10_000) / 100;
    const trimmed = m.toFixed(2).replace(/\.?0+$/, '');
    return `$${trimmed}M`;
  }
  if (abs >= 1_000) return `$${Math.round(num / 1000)}K`;
  return `$${num}`;
}

function formatTimestamp(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const now = new Date();
  const sec = (now - d) / 1000;
  if (sec < 60) return 'just now';
  if (sec < 3600) return `${Math.floor(sec / 60)} min ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)} h ago`;
  if (sec < 7 * 86400) return `${Math.floor(sec / 86400)} d ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// styles
const pageStyle = { padding: '32px 56px 48px', maxWidth: '1280px', margin: '0 auto', fontFamily: 'var(--font)', color: 'var(--ink)' };

const heroStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '24px' };
const eyebrowStyle = { fontSize: '13.5px', color: 'var(--warm)', fontStyle: 'italic' };
const titleStyle = { fontFamily: 'var(--display)', fontWeight: 500, fontSize: '40px', letterSpacing: '-0.02em', margin: '6px 0 0', color: 'var(--ink)' };
const metaStyle = { fontSize: '13px', color: 'var(--muted)', marginTop: '8px' };

const ctaStyle = {
  display: 'inline-flex', alignItems: 'center', gap: '8px',
  height: '50px', padding: '0 24px', borderRadius: '999px',
  background: 'var(--ink)', color: 'var(--paper)', border: '1px solid var(--ink)',
  fontSize: '14.5px', fontWeight: 500, cursor: 'pointer', fontFamily: 'var(--font)',
};

const menuWrapStyle = { position: 'relative' };
const menuPanelStyle = {
  position: 'absolute',
  top: 'calc(100% + 8px)',
  right: 0,
  minWidth: '280px',
  padding: '6px',
  background: 'var(--paper)',
  border: '1px solid var(--line)',
  borderRadius: '8px',
  boxShadow: 'var(--shadow)',
  zIndex: 20,
};
const menuItemStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '4px',
  width: '100%',
  padding: '12px 14px',
  border: 'none',
  borderRadius: '6px',
  background: 'transparent',
  cursor: 'pointer',
  fontFamily: 'var(--font)',
  textAlign: 'left',
  color: 'var(--ink)',
};
const menuItemTitleStyle = { fontSize: '14px', fontWeight: 600, color: 'var(--ink)' };
const menuItemBodyStyle = { fontSize: '12.5px', color: 'var(--ink2)', lineHeight: 1.45 };

const statsRowStyle = { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px', marginBottom: '24px' };
const statStyle = { paddingTop: '18px', borderTop: '1px solid var(--line)' };
const statLabelStyle = { fontSize: '12.5px', color: 'var(--muted)' };
const statValueStyle = { fontFamily: 'var(--font)', fontSize: '28px', fontWeight: 600, letterSpacing: '-0.02em', marginTop: '4px', fontVariantNumeric: 'tabular-nums' };
const statValueDisplayStyle = { ...statValueStyle, fontFamily: 'var(--display)', fontStyle: 'italic', color: 'var(--accent)' };

const tableStyle = { background: 'var(--paper)', border: '1px solid var(--line)', borderRadius: '4px', overflow: 'hidden' };
const rowStyle = { display: 'grid', gridTemplateColumns: '2fr 130px 140px 110px 340px', gap: '14px', padding: '14px 22px', alignItems: 'center', borderBottom: '1px solid var(--line)', fontSize: '13px' };
const headRowStyle = { color: 'var(--muted)', fontSize: '11.5px', textTransform: 'uppercase', letterSpacing: '0.05em', background: 'var(--bg)' };

const nameBtnStyle = { background: 'transparent', border: 'none', padding: 0, textAlign: 'left', cursor: 'pointer', fontFamily: 'var(--font)', color: 'var(--ink)' };
const nameStyle = { fontWeight: 500, fontSize: '14.5px' };
const subStyle = { fontSize: '12px', color: 'var(--muted)', marginTop: '2px', fontVariantNumeric: 'tabular-nums' };

const tierChipStyle = { display: 'inline-flex', alignItems: 'center', padding: '3px 10px', borderRadius: '999px', fontSize: '11.5px', fontWeight: 500, justifySelf: 'start' };
const amountStyle = { fontVariantNumeric: 'tabular-nums', fontWeight: 600, fontSize: '15px', textAlign: 'right' };
const timeStyle = { fontSize: '12px', color: 'var(--ink2)' };

const actionsCellStyle = { display: 'flex', gap: '6px', flexWrap: 'wrap' };
const actionBtnBase = {
  display: 'inline-flex', alignItems: 'center', height: '28px',
  padding: '0 10px', borderRadius: '999px',
  fontSize: '12px', cursor: 'pointer', fontFamily: 'var(--font)',
  border: '1px solid var(--line)', background: 'var(--surface)', color: 'var(--ink2)',
};
const primaryActionStyle = { ...actionBtnBase, background: 'var(--accent)', color: '#fff', border: '1px solid var(--accent)', fontWeight: 500 };
const secondaryActionStyle = actionBtnBase;
const actionDisabledStyle = { ...actionBtnBase, color: 'var(--muted)', cursor: 'not-allowed', opacity: 0.7 };
const dangerActionStyle = { ...actionBtnBase, color: 'var(--warm)' };

const emptyStyle = {
  padding: '48px 32px',
  textAlign: 'center',
  background: 'var(--accent-bg)',
  border: '1px solid var(--accent-bg2)',
  borderRadius: '8px',
};
const emptyEyebrowStyle = { fontSize: '12px', color: 'var(--accent-strong)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em' };
const emptyHeroStyle = { margin: '8px 0 12px', fontSize: '22px', fontWeight: 600, color: 'var(--ink)', letterSpacing: '-0.015em' };
const emptyBodyStyle = { margin: '0 auto 20px', fontSize: '14px', color: 'var(--ink2)', lineHeight: 1.55, maxWidth: '520px' };
const emptyPathsStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
  gap: '14px',
  maxWidth: '760px',
  margin: '0 auto',
  textAlign: 'left',
};
const emptyPathBaseStyle = {
  padding: '22px 24px',
  borderRadius: '8px',
  background: 'var(--surface)',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'var(--line)',
  cursor: 'pointer',
  fontFamily: 'var(--font)',
  color: 'var(--ink)',
  textAlign: 'left',
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
};
const emptyPathPrimaryStyle = { ...emptyPathBaseStyle };
const emptyPathSecondaryStyle = { ...emptyPathBaseStyle };
const emptyPathTitleStyle = { fontSize: '16px', fontWeight: 600, color: 'var(--ink)' };
const emptyPathBodyStyle = { fontSize: '13px', color: 'var(--ink2)', lineHeight: 1.55 };
const emptyPathArrowStyle = { fontSize: '13px', color: 'var(--accent)', fontWeight: 500, marginTop: 'auto' };

const errorStyle = { padding: '12px 14px', background: 'var(--warm-bg)', color: 'var(--warm)', borderRadius: '4px', fontSize: '13px', marginBottom: '20px' };
const bannerStyle = {
  padding: '12px 14px',
  background: 'var(--accent-bg)',
  color: 'var(--ink)',
  borderRadius: '4px',
  fontSize: '13px',
  marginBottom: '20px',
  lineHeight: 1.5,
};
const inlineLinkStyle = { background: 'transparent', border: 'none', color: 'var(--warm)', textDecoration: 'underline', cursor: 'pointer', fontFamily: 'var(--font)', font: 'inherit' };
